import { useRef } from "react";
import { motion, useInView } from "framer-motion";
import { CONTACT } from "../lib/brand";

const WA = (msg) =>
  `https://api.whatsapp.com/send/?phone=5541984040423&text=${encodeURIComponent(msg)}`;

const DEPTS = [
  {
    key: "femininos",
    n: "01",
    label: "Desapegos Femininos",
    caption: "Alfaiataria, festa, denim, alfaias & acessórios de autor.",
    img: "https://images.unsplash.com/photo-1490481651871-ab68de25d43d?w=1200&auto=format&fit=crop&q=80",
    href: WA("Olá, Herida! Quero conhecer os Desapegos Femininos."),
    type: "link",
  },
  {
    key: "masculinos",
    n: "02",
    label: "Desapegos Masculinos",
    caption: "Terno, camisaria, jaquetas, sapataria e peças de assinatura.",
    img: "https://images.unsplash.com/photo-1594938298603-c8148c4dae35?w=1200&auto=format&fit=crop&q=80",
    href: WA("Olá, Herida! Quero conhecer os Desapegos Masculinos."),
    type: "link",
  },
  {
    key: "infantis",
    n: "03",
    label: "Desapegos Infantis",
    caption: "Peças com afeto para meninas e meninos — do casual ao party.",
    img: "https://images.unsplash.com/photo-1518831959646-742c3a14ebf7?w=1200&auto=format&fit=crop&q=80",
    href: WA("Olá, Herida! Quero conhecer os Desapegos Infantis."),
    type: "link",
  },
  {
    key: "decor",
    n: "04",
    label: "Decorações & Antiguidades",
    caption: "Peças únicas para composições autorais em casa. Assista o tour em vídeo.",
    img: "https://images.unsplash.com/photo-1513519245088-0e12902e5a38?w=1200&auto=format&fit=crop&q=80",
    href: CONTACT.decor,
    type: "video",
  },
];

function Play() {
  return (
    <svg
      viewBox="0 0 24 24"
      width="22"
      height="22"
      fill="currentColor"
      aria-hidden="true"
      className="ml-1"
    >
      <path d="M8 5.14v13.72a1 1 0 001.55.83l10.03-6.86a1 1 0 000-1.66L9.55 4.31A1 1 0 008 5.14z" />
    </svg>
  );
}

function Card({ d, i, inView }) {
  const isVideo = d.type === "video";
  return (
    <motion.a
      href={d.href}
      target="_blank"
      rel="noreferrer"
      aria-label={d.label}
      data-testid={`produto-${d.key}`}
      initial={{ opacity: 0, y: 40 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.9, delay: 0.15 + i * 0.1, ease: [0.22, 1, 0.36, 1] }}
      whileHover={{ y: -6 }}
      className="group relative flex flex-col bg-cream-50 border border-ink-800/15 hover:border-brick-500 transition-colors duration-500 overflow-hidden"
    >
      <div className="relative aspect-[4/5] overflow-hidden bg-cream-200">
        <img
          src={d.img}
          alt={d.label}
          loading="lazy"
          className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1.4s] ease-out group-hover:scale-105"
        />
        <div
          aria-hidden
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(180deg, rgba(26,24,24,0) 40%, rgba(26,24,24,0.55) 100%)",
          }}
        />

        {/* Corner marks */}
        <div className="absolute top-3 left-3 right-3 flex justify-between font-mono text-[9px] uppercase tracking-[0.3em] text-cream">
          <span>{d.n} · Dept.</span>
          <span>{isVideo ? "HL / Decor" : "Curadoria"}</span>
        </div>

        {/* Play badge only on video card */}
        {isVideo && (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="relative w-20 h-20 sm:w-24 sm:h-24 flex items-center justify-center">
              <motion.span
                aria-hidden
                className="absolute inset-0 rounded-full border border-cream"
                animate={{ scale: [1, 1.35, 1], opacity: [0.6, 0, 0.6] }}
                transition={{ duration: 2.4, repeat: Infinity, ease: "easeOut" }}
              />
              <span className="relative w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-brick-500 text-cream flex items-center justify-center group-hover:bg-cream group-hover:text-brick-500 transition-colors duration-500">
                <Play />
              </span>
            </div>
          </div>
        )}

        {/* Bottom label overlay */}
        <div className="absolute left-4 right-4 bottom-4 flex items-end justify-between gap-4">
          <span
            className="font-serif italic text-cream leading-none"
            style={{ fontSize: "clamp(1.5rem, 2vw, 2rem)" }}
          >
            {d.label}
          </span>
        </div>
      </div>

      <div className="flex items-start justify-between gap-4 px-5 py-5 border-t border-ink-800/10">
        <p className="font-sans text-sm leading-relaxed text-ink-800/75 max-w-xs">
          {d.caption}
        </p>
        <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-brick-500 whitespace-nowrap flex items-center gap-2 group-hover:translate-x-1 transition-transform duration-500">
          {isVideo ? "Assistir" : "WhatsApp"} →
        </span>
      </div>
    </motion.a>
  );
}

export default function Produtos() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-10% 0px" });

  return (
    <section
      id="produtos"
      ref={ref}
      data-testid="produtos-section"
      className="relative bg-cream-100 py-24 sm:py-40 border-t border-ink-800/15"
    >
      <div className="mx-auto max-w-[1600px] px-6 sm:px-10">
        <div className="grid grid-cols-12 gap-6 lg:gap-16 items-end mb-16 sm:mb-20">
          <div className="col-span-12 lg:col-span-7">
            <div className="flex items-center gap-4 font-mono text-[10px] uppercase tracking-[0.3em] text-ink-800/60 mb-8">
              <span className="w-8 h-px bg-brick-500" />
              <span>N.º 006 — Departamentos</span>
            </div>
            <h2
              className="font-serif leading-[0.9] tracking-tight text-ink-800"
              style={{ fontSize: "clamp(3rem, 7.5vw, 7.5rem)" }}
            >
              Quatro mundos, <br />
              <span className="italic-serif text-brick-500">uma só curadoria</span>.
            </h2>
          </div>
          <div className="col-span-12 lg:col-span-4 lg:col-start-9">
            <p className="font-sans text-base sm:text-lg leading-relaxed text-ink-800/70">
              Do guarda-roupa à sala de estar — minha curadoria abre quatro portas.
              Toque em um departamento para conversar direto comigo no WhatsApp.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 sm:gap-6">
          {DEPTS.map((d, i) => (
            <Card key={d.key} d={d} i={i} inView={inView} />
          ))}
        </div>
      </div>
    </section>
  );
}
